import React, { useState } from 'react';
import { Box, Paper, Typography } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import WarningIcon from '@material-ui/icons/Warning';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import { makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
        width: 'fit-content',
        margin: '0 auto',
        '& .count': {
            zIndex: 2,
            // @ts-ignore
            display: ({ count }) => (count > 0 ? 'flex' : 'none'),
            justifyContent: 'center',
            alignItems: 'center',
            width: '25px',
            height: '25px',
            padding: '0.25rem',
            position: 'absolute',
            top: '-7px',
            right: '7px',
            border: '1px solid rgba(150, 127, 14, 0.25)',
            borderRadius: '50%',
            backgroundColor: 'rgba(230, 230, 16, 1)',
            boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
        },
        '& .count > span': {
            fontWeight: '700',
            fontSize: '11px',
            textAlign: 'center',
            color: 'black',
        },
    },
    customCardRoot: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        margin: '1rem',
        '&:hover': {
            cursor: 'pointer',
        },
    },
    customCardHeader: {
        width: '100%',
        // @ts-ignore
        backgroundColor: ({ color }) => color,
        padding: '0.5rem 2rem',
        height: '20%',
        fontSize: '0.95rem',
        fontWeight: 900,
        textTransform: 'uppercase',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    customCardData: {
        width: '100%',
        height: '100%',
        padding: '0.5rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        '& > *': {
            margin: '0.25rem 0',
        },
    },
    between: {
        width: '100%',
        justifyContent: 'space-between',
    },
}));

interface CustomCardProps {
    color: string;
    title: string;
    count: number;
    handleClick: () => void;
    data: {
        pending: number;
        solved: number;
        ontime: number;
        due: number;
        overdue: number;
    };
}

const CustomCard = ({ color, title, count, handleClick, data }: CustomCardProps) => {
    const [elevation, setElevation] = useState(2);
    const classes = useStyles({ color, count });

    return (
        <Box className={classes.root} onClick={handleClick}>
            <Paper
                className={classes.customCardRoot}
                elevation={elevation}
                onMouseOver={() => setElevation(8)}
                onMouseOut={() => setElevation(2)}
            >
                <Box className={classes.customCardHeader}>
                    <Box>{title}</Box>
                </Box>
                <Box className={classes.customCardData}>
                    <Box textAlign="center">
                        <Typography variant="h6" gutterBottom style={{ fontSize: 24, fontWeight: 'bold' }}>
                            Seja bem-vindo(a) ao Canal da Ouvidoria do Município de Capela do Alto.
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom style={{ fontSize: 20, fontWeight: 'bold' }}>
                            Prezado(a) Diretor(a) e responsável,
                        </Typography>
                        <Typography paragraph style={{ fontSize: 20, fontWeight: 'bold' }}>
                            A Ouvidoria é o canal onde o cidadão confia sua voz à gestão pública. Cada manifestação recebida representa uma oportunidade de demonstrar organização, compromisso e responsabilidade.
                        </Typography>
                        <Typography paragraph style={{ fontSize: 20, fontWeight: 'bold' }}>
                            Seu departamento tem papel essencial nesse processo. Cada resposta enviada representa a postura da sua equipe, a eficiência da sua liderança e o compromisso da gestão com a transparência.
                        </Typography>
                        <Typography paragraph style={{ fontSize: 20, fontWeight: 'bold' }}>
                            Responder dentro do prazo legal de até 30 dias é mais do que cumprir uma norma — é demonstrar respeito ao cidadão e fortalecer a credibilidade da gestão.
                        </Typography>
                        <Typography paragraph style={{ fontSize: 20, fontWeight: 'bold', color: '#0070c0' }}>
                            A força da gestão municipal passa pelo trabalho de cada diretor (a) e responsável. Seu papel é estratégico. Sua atuação faz a diferença. Sua resposta constrói a imagem de uma administração sólida e transparente.
                            Vamos juntos transformar cada manifestação em uma oportunidade de excelência no serviço público e de fortalecimento da confiança do cidadão de Capela do Alto.
                            Contamos com seu comprometimento.
                        </Typography>
                        <Box mt={2}>
                            <Typography variant="body1" style={{ fontSize: 20, fontWeight: 'bold' }}>
                                Atenciosamente,
                            </Typography>
                            <Typography variant="body1" style={{ fontSize: 20, fontWeight: 'bold' }}>
                                Francine
                            </Typography>
                            <Typography variant="body2" style={{ fontSize: 20, fontWeight: 'bold' }}>
                                Ouvidora Geral do Município
                            </Typography>
                        </Box>
                    </Box>
                    <Box display="flex" className={classes.between}>
                        <Box display="flex" alignItems="center">
                            <HelpOutlineIcon />
                            <Box fontWeight={500} ml={2}>
                                Pendente:
                            </Box>
                            <Box fontWeight={700} ml={2}>
                                {data.pending ? data.pending : 0}
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <ThumbUpIcon />
                            <Box fontWeight={500} ml={2}>
                                Dentro do prazo:
                            </Box>
                            <Box fontWeight={700} ml={2}>
                                {data.ontime ? data.ontime : 0}
                            </Box>
                        </Box>
                    </Box>
                    <Box display="flex" className={classes.between}>
                        <Box display="flex" alignItems="center">
                            <WarningIcon />
                            <Box fontWeight={500} ml={2}>
                                A vencer:
                            </Box>
                            <Box fontWeight={700} ml={2}>
                                {data.due ? data.due : 0}
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <ThumbDownIcon />
                            <Box fontWeight={500} ml={2}>
                                Vencidas:
                            </Box>
                            <Box fontWeight={700} ml={2}>
                                {data.overdue ? data.overdue : 0}
                            </Box>
                        </Box>
                    </Box>
                    <Box display="flex">
                        <Box display="flex" alignItems="center">
                            <CheckBoxIcon />
                            <Box fontWeight={500} ml={2}>
                                Respondidas:
                            </Box>
                            <Box fontWeight={700} ml={2}>
                                {data.solved ? data.solved : 0}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Alert severity="info" style={{ width: '100%', marginTop: 'auto', borderRadius: 0 }}>
                    Clique no card para acessar a ouvidoria.
                </Alert>
            </Paper>
            {count > 0 ? <div className="count">
                <span>{count > 9 ? '9+' : count}</span>
            </div> : null}
        </Box>
    );
};

export default CustomCard;
